import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Lock, Eye, EyeOff, AlertCircle, Info } from 'lucide-react';
import api from '../../utils/api';
import useAuthStore from '../../store/authStore';
import useSettingsStore from '../../store/settingsStore';
import { Button } from '../../components/ui';
import LanguageSwitcher from '../../components/LanguageSwitcher';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { portalName, logoUrl, loginBgUrl } = useSettingsStore();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // A custom institution background image keeps the original dark-glass
  // treatment (designed for contrast over a busy photo). Without one — the
  // default, which is this deployment's actual state — the login screen
  // bridges into the marketing site's warm brand language instead of a
  // generic dark SaaS auth card.
  const hasCustomBg = !!loginBgUrl;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.username.trim() || !formData.password.trim()) {
      setError(t('login.errors.missingFields'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', {
        username: formData.username.trim(),
        password: formData.password
      });
      const { user, token } = res.data.data;
      login(user, token);

      if (user.role === 'SUPER_ADMIN') navigate('/super-admin/dashboard');
      else if (user.role === 'ADMIN') navigate('/admin/dashboard');
      else if (user.role === 'STUDENT') navigate('/student/dashboard');
      else if (user.role === 'PROCTOR') navigate('/proctor/dashboard');
      else navigate('/login');
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        t('login.errors.invalidCredentials');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const fieldClasses = (hasError) =>
    hasCustomBg
      ? `block w-full pl-10 pr-4 py-3 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-themePrimary focus:border-transparent transition-colors text-sm ${
          hasError ? 'border-red-400 bg-red-900/30' : 'border-white/20 bg-white/5'
        }`
      : `block w-full pl-10 pr-4 py-3 border rounded-lg text-[#241f1a] placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-[#1e8790]/40 focus:border-[#1e8790] transition-colors text-sm ${
          hasError ? 'border-red-400 bg-red-50' : 'border-slate-300'
        }`;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={
        hasCustomBg
          ? {
              backgroundImage: `url(${loginBgUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : { backgroundColor: '#faf6ea' }
      }
    >
      {/* Overlay (custom-bg mode only) */}
      {hasCustomBg && <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.55)' }} />}

      {/* Language switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher tone={hasCustomBg ? 'dark' : 'light'} />
      </div>

      {/* Decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {hasCustomBg ? (
          <>
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-themePrimary rounded-full opacity-10 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-themePrimary rounded-full opacity-10 blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-themePrimary rounded-full opacity-5 blur-3xl" />
          </>
        ) : (
          <>
            <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full blur-3xl opacity-50" style={{ background: 'radial-gradient(circle at 30% 30%, #e0bb49, transparent 70%)' }} />
            <div className="absolute top-1/4 -right-24 w-72 h-72 rounded-full blur-3xl opacity-40" style={{ background: 'radial-gradient(circle at 30% 30%, #d98fa0, transparent 70%)' }} />
            <div className="absolute -bottom-32 left-1/4 w-80 h-80 rounded-full blur-3xl opacity-40" style={{ background: 'radial-gradient(circle at 30% 30%, #1e8790, transparent 70%)' }} />
          </>
        )}
      </div>

      <div className="relative w-full max-w-md z-10">
        {/* Card */}
        <div
          className={
            hasCustomBg
              ? 'bg-themeSecondary rounded-2xl shadow-2xl overflow-hidden border border-white/10'
              : 'bg-white rounded-2xl shadow-2xl overflow-hidden border border-black/5'
          }
        >
          {/* Card Header */}
          <div
            className={
              hasCustomBg
                ? 'bg-themePrimary px-8 py-8 text-center relative overflow-hidden'
                : 'px-8 pt-9 pb-7 text-center relative overflow-hidden'
            }
            style={hasCustomBg ? undefined : { background: 'linear-gradient(135deg, #fbf3dd, #faf6ea)' }}
          >
            {hasCustomBg && (
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            )}
            <div className="relative">
              <div
                className={
                  hasCustomBg
                    ? 'inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-2xl mb-4 overflow-hidden shadow-lg ring-2 ring-white/30'
                    : 'inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 overflow-hidden shadow-lg ring-4 ring-white'
                }
              >
                <img src={logoUrl || '/assets/logo.png'} alt={t('common.logoAlt', { portalName: portalName || t('common.appName') })} className="w-full h-full object-cover" />
              </div>
              <h1
                className={hasCustomBg ? 'text-2xl font-bold text-white truncate max-w-[300px] mx-auto drop-shadow' : 'font-fraunces text-3xl font-bold text-[#241f1a] truncate max-w-[300px] mx-auto'}
              >
                {portalName || t('common.appName')}
              </h1>
              <p className={hasCustomBg ? 'text-indigo-200 text-sm mt-1' : 'text-[#4a433b] text-sm mt-1'}>
                {t('login.subtitle')}
              </p>
            </div>
          </div>

          {/* Card Body */}
          <div className="px-8 py-8">
            <p className={hasCustomBg ? 'text-sm text-white/70 mb-6' : 'text-sm text-[#4a433b] mb-6'}>
              {t('login.signInPrompt')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Inline Error Alert */}
              {error && (
                <div
                  className={
                    hasCustomBg
                      ? 'flex items-start gap-3 bg-red-900/30 border border-red-500/40 text-red-200 rounded-lg px-4 py-3'
                      : 'flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3'
                  }
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}

              {/* Username */}
              <div>
                <label htmlFor="username" className={hasCustomBg ? 'block text-sm font-medium text-white/90 mb-1.5' : 'block text-sm font-medium text-[#241f1a] mb-1.5'}>
                  {t('login.usernameLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <User className="w-5 h-5 text-slate-400" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder={t('login.usernamePlaceholder')}
                    className={fieldClasses(!!error)}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className={hasCustomBg ? 'block text-sm font-medium text-white/90 mb-1.5' : 'block text-sm font-medium text-[#241f1a] mb-1.5'}>
                  {t('login.passwordLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-slate-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t('login.passwordPlaceholder')}
                    className={`${fieldClasses(!!error)} pr-10`}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button type="submit" variant={hasCustomBg ? 'primary' : 'sticker'} fullWidth loading={loading} size="lg">
                {loading ? t('login.signingIn') : t('login.signIn')}
              </Button>
            </form>

            {/* Info note */}
            <div
              className={
                hasCustomBg
                  ? 'mt-5 flex items-start gap-2 text-xs text-white/60 bg-black/20 rounded-lg px-3 py-2.5 border border-white/5'
                  : 'mt-5 flex items-start gap-2 text-xs text-[#4a433b] bg-[#faf6ea] rounded-lg px-3 py-2.5 border border-black/5'
              }
            >
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
              <span>{t('login.helpNote')}</span>
            </div>
          </div>
        </div>

        <p className={hasCustomBg ? 'text-center text-white/50 text-xs mt-5' : 'text-center text-[#4a433b] text-xs mt-5'}>
          {t('login.allRightsReserved', { year: new Date().getFullYear(), portalName: portalName || t('common.appName') })}
        </p>
      </div>
    </div>
  );
};

export default Login;
